import React, { useState } from 'react';
import { Table, TableColumn } from '@backstage/core-components';
import {
  Box,
  Button,
  CircularProgress,
  Link,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  IconButton,
  Typography,
  Chip,
} from '@material-ui/core';
import HelpIcon from '@material-ui/icons/Help';
import { TriggerTableProps, TriggerExecutionFormState } from './types';
import { createSubstitutionsObject, parseResourceName } from './utils';
import { TriggerExecutionDrawer } from './TriggerExecutionDrawer';
import { errorApiRef, useApi, configApiRef } from '@backstage/core-plugin-api';
import {
  TriggerPayload,
  TriggerListItem,
  RunTriggerOptions,
} from '@internal/backstage-plugin-cloudbuild-common';
import { useEntity } from '@backstage/plugin-catalog-react';
import { stringifyEntityRef } from '@backstage/catalog-model';

export const TriggerTable: React.FC<TriggerTableProps> = ({
  triggers,
  onExecuteTrigger,
  executingTrigger,
  defaultBranch = 'main',
}) => {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const [currentRowId, setCurrentRowId] = useState<string | null>(null);
  const [currentTrigger, setCurrentTrigger] = useState<TriggerPayload>();
  const [helpDialogOpen, setHelpDialogOpen] = useState(false);
  const errorApi = useApi(errorApiRef);
  const configApi = useApi(configApiRef);
  const { entity } = useEntity();

  const backstageCiCdDocsUrl =
    configApi.getOptionalString('techdocsUrl.backstageCiCd') || undefined;
  const gcbChatbotDocsUrl =
    configApi.getOptionalString('techdocsUrl.gcbChatbot') || undefined;

  const handleOpen = (rowId: string, trigger?: TriggerPayload) => {
    setDrawerOpen(true);
    setCurrentRowId(rowId);
    setCurrentTrigger(trigger);
  };

  const handleClose = () => {
    setDrawerOpen(false);
    setCurrentRowId(null);
    setCurrentTrigger(undefined);
  };

  const handleExecute = async (
    triggerExecutionFormState: TriggerExecutionFormState,
  ) => {
    if (!currentTrigger?.resourceName) {
      errorApi.post({
        name: 'Error',
        message: `Invalid trigger configuration`,
      });
      return;
    }
    const substitutionsObject = createSubstitutionsObject(
      triggerExecutionFormState.substitutions,
    );

    const parsedResource = parseResourceName(currentTrigger.resourceName);
    if (!parsedResource) {
      errorApi.post({
        name: 'Error',
        message: 'Invalid resource name format',
      });
      return;
    }

    try {
      const runTriggerOptions: RunTriggerOptions = {
        projectId: parsedResource.projectId,
        triggerId: parsedResource.triggerId,
        source: {
          ...(triggerExecutionFormState.revisionType === 'branch' && {
            branchName: triggerExecutionFormState.branchName,
          }),
          ...(triggerExecutionFormState.revisionType === 'commit' && {
            commitSha: triggerExecutionFormState.commitHash,
          }),
          ...(triggerExecutionFormState.revisionType === 'tag' && {
            tagName: triggerExecutionFormState.tagName,
          }),
        },
        entityRef: stringifyEntityRef(entity),
        ...(substitutionsObject && { substitutions: substitutionsObject }),
      };

      await onExecuteTrigger(currentRowId!, runTriggerOptions);
      handleClose();
    } catch (err) {
      errorApi.post({
        name: 'Error',
        message:
          err instanceof Error ? err.message : 'Failed to execute trigger',
      });
    }
  };

  const columns: TableColumn<TriggerListItem>[] = [
    {
      title: 'Name',
      field: 'name',
      render: row =>
        row.consoleUrl ? (
          <Link
            href={row.consoleUrl}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            color="primary"
            aria-label={`Visit ${row.name} on Google Cloud console`}
          >
            {row.name}
          </Link>
        ) : (
          row.name
        ),
    },
    { title: 'Description', field: 'description' },
    {
      title: 'Tags',
      field: 'tags',
      render: row => (
        <Box display="flex" flexWrap="wrap" style={{ gap: 4 }}>
          {row.tags?.map((tag, index) => (
            <Chip key={index} label={tag} size="small" variant="outlined" />
          ))}
        </Box>
      ),
    },
    {
      title: 'Repository',
      field: 'repository.name',
      render: row =>
        row.repository.url ? (
          <Link
            href={row.repository.url}
            target="_blank"
            rel="noopener noreferrer"
            underline="hover"
            color="primary"
            aria-label={`Visit ${row.repository.url}`}
          >
            {`${
              row.repository.owner
                ? `${row.repository.owner}/${row.repository.name}`
                : row.repository.name
            }`}
          </Link>
        ) : (
          row.repository.name
        ),
    },
    {
      title: 'Status',
      field: 'status',
      render: row => (
        <span
          style={{
            color: row.status === 'ENABLED' ? 'green' : 'grey',
            fontWeight: 'bold',
          }}
        >
          {row.status}
        </span>
      ),
    },
    {
      title: 'Actions',
      field: 'actions',
      render: row => (
        <>
          {row.eventFilterType === 'push' && (
            <Button
              color="primary"
              variant="contained"
              onClick={() => handleOpen(row.id, row.trigger)}
              disabled={
                row.status === 'DISABLED' || executingTrigger === row.id
              }
              aria-label={`Run ${row.name} trigger`}
            >
              {executingTrigger === row.id ? (
                <>
                  <CircularProgress size={20} color="inherit" />
                  <Box ml={1}>Running...</Box>
                </>
              ) : (
                'Run'
              )}
            </Button>
          )}
          {row.eventFilterType === 'pr' && (
            <span
              style={{
                color: 'grey',
                minHeight: '35px',
                display: 'flex',
                alignItems: 'center',
              }}
            >
              Auto-triggered
            </span>
          )}
        </>
      ),
    },
  ];

  return (
    <>
      <Table
        title={
          <Box display="flex" alignItems="center">
            <span>Triggers</span>
            <IconButton
              size="small"
              onClick={() => setHelpDialogOpen(true)}
              aria-label="Help"
              style={{ marginLeft: 8 }}
            >
              <HelpIcon />
            </IconButton>
          </Box>
        }
        options={{
          search: true,
          paging: true,
          pageSize: 10,
          padding: 'dense',
        }}
        columns={columns}
        data={triggers}
      />

      <TriggerExecutionDrawer
        open={drawerOpen}
        onClose={handleClose}
        trigger={currentTrigger}
        onExecute={handleExecute}
        isExecuting={executingTrigger === currentRowId}
        defaultBranch={defaultBranch}
      />

      <Dialog
        open={helpDialogOpen}
        onClose={() => setHelpDialogOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Cloud Build Triggers Help</DialogTitle>
        <DialogContent>
          <Typography variant="body1" paragraph>
            Cloud Build triggers automatically run builds in response to events
            in your repository, such as pushes or pull requests.
          </Typography>
          <Typography variant="body2" paragraph>
            For more information, visit the documentation:
          </Typography>
          <Box display="flex" flexDirection="column" style={{ gap: 8 }}>
            {backstageCiCdDocsUrl && (
              <Link
                href={backstageCiCdDocsUrl}
                color="primary"
                underline="hover"
              >
                Backstage CI/CD Documentation
              </Link>
            )}
            {gcbChatbotDocsUrl && (
              <Link href={gcbChatbotDocsUrl} color="primary" underline="hover">
                Google Chatbot Documentation
              </Link>
            )}
            {!backstageCiCdDocsUrl && !gcbChatbotDocsUrl && (
              <Typography variant="body2" color="textSecondary">
                Documentation links are not configured.
              </Typography>
            )}
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHelpDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
};
